import { OrderHistory } from '../../stores/appOrderHistoryStore';
import { RevenueHistory } from '../../services/revenuehistory/revenueHistoryService';
import { CostPriceAnalysis } from '../../services/costPrice/costPriceService';
import { Subrecipe } from '../../services/subrecipes/subrecipesService';
import { InventoryCount, InventoryCountItem } from '../../services/inventory/inventoryService';
import {
  groupCountsIntoSessions,
  SessionGroup,
} from '../../components/pages/inventory/inventoryOverviewUtils';
import { getTableFormattedDate } from '../dateUtils';
import { baseProductKey } from '../inventoryProductKey';
import {
  inventoryKey,
  latestPriceForKey,
  collectWindowPurchases,
  purchasedQtyForCount,
  neededQtyForCount,
  frozenCountSpace,
  foldIntoCountSpace,
  alignCountSpaces,
  FrozenCountSpace,
  ConsumptionPoint,
  ConsumptionCustomRow,
  ConsumptionWindowInfo,
  WindowPurchase,
} from './consumptionReport';
import {
  buildNeededIndex,
  collectNeedProductInfo,
  extractSalesFromRevenue,
  NeededInfo,
  NeedProductInfo,
  PerUnitNeedsIndex,
  PerUnitNeed,
} from './utilizationReport';
import { AllocationNeed, collectPools } from './allocationEngine';
import { explodeSubrecipeToProductNeeds, addNeedPath, finalizeNeedPaths, NeedPath } from './recipeExplosion';

/**
 * Pure, React-free computation for the Leakage (inventory variance) tab — the
 * sibling of `consumptionReport.ts` (R7). Same equation, rearranged:
 *
 *   expectedQty = startQty + purchasedQty − neededQty   (per product, std units)
 *   varianceQty = endQty − expectedQty                  (negative = leakage / loss)
 *
 * i.e. `variance = −trueWaste` from R7. Given a group + a "viewing" (end) session
 * and an older baseline session of the SAME group, we reconcile every product,
 * value the gap at the end-count frozen €/std-unit (price chain below), attribute
 * it to a section where possible, and surface the data-quality flags that tell the
 * user how much to trust each row.
 *
 * Scope: a PRODUCT row exists only for a product that appears in one of the two
 * counts, and reads purchases for counted keys only — an untracked product's
 * consumption is not leakage. A POOLED row is the deliberate exception: a primary
 * and its alternatives are one interchangeable supply, so the row takes purchases
 * from EVERY member while its counts can only come from the counted ones. For an
 * uncounted member that is arithmetically the same as assuming it ended with zero
 * stock, which over-reports leakage rather than hiding it — the honest failure
 * direction, declared by `pool.membersUncounted` + `confidence: 'low'`, and it
 * points at the fix (count the sibling). Custom ingredients,
 * products without a usable price ("not linked") and coverage-excluded products
 * are assumed to have zero leakage: they stay in the frozen start/counted totals
 * (so those match the Count Review page exactly) and their count change flows
 * into `adjustmentValue` so the reconciliation row still adds up.
 *
 * Everything is computed client-side from the data the inventory page already
 * loads (orders, revenue, cost prices, subrecipes, counts history). Reuses the
 * inventory-key matching + latest-price walk from `consumptionReport.ts` and the
 * needed-index walker from `utilizationReport.ts` so the three stay in lock-step.
 *
 * See `z_ralph/tasks/1_new_tasks/inventorycount/spec-leakage-overview.md` for the
 * decided rules (valuation, attribution, coverage guard, confidence, status).
 */

// --- tunable thresholds (exported for tests, overview §11) -----------------

export const VARIANCE_OK_EUR = 10;
export const VARIANCE_OK_PCT = 0.05;
export const VARIANCE_HIGH_RISK_EUR = 50;

/** Float-equality guard — "unchanged" section deltas are exact, this only kills FP noise. */
const EPS = 1e-9;

/** Synthetic section key for the "could not be attributed" bucket. */
export const UNATTRIBUTED_KEY = '__unattributed__';

// --- selection ------------------------------------------------------------

export interface VarianceSelection {
  /** groupId ?? groupName ?? 'ungrouped' — same bucketing as R7's groupKeyOf. */
  groupKey: string;
  /** SessionGroup.key of the "viewing" session (page header count selector). */
  endSessionKey: string;
  /** SessionGroup.key of the older session to compare against. */
  baselineSessionKey: string;
}

// --- output types ---------------------------------------------------------

export type VarianceConfidence = 'high' | 'medium' | 'low';
export type VarianceStatus = 'ok' | 'review' | 'highRisk' | 'overCounted' | 'lowConfidence';

/** One section's slice of a product (facts — always computed). */
export interface VarianceSectionChange {
  sectionKey: string;
  sectionName: string;
  deleted: boolean;
  startQty: number;
  endQty: number;
  deltaQty: number; // end − start
  deltaValue: number | null;
  /**
   * € held in this section at the end count, carried explicitly whenever `endQty ×
   * row.price` would be wrong: on POOLED rows (each member prices its own quantities, and
   * they may not even share a unit) and on a row whose two counts were frozen in
   * unbridgeable spaces (`countQtyUnreadable` — the quantities above are placeholder
   * zeroes there, the frozen € is the fact). Undefined on an ordinary product row, where
   * the section value is exactly `endQty × pricePerStdUnit`. PRESENT is the signal to
   * prefer it — do not branch on `row.pool` to decide.
   */
  endValue?: number | null;
}

// --- alternative pools ----------------------------------------------------

/** Prefix of a pooled row's `key` (`pool:<canonical member key>`) — never a product key. */
export const VARIANCE_POOL_KEY_PREFIX = 'pool:';

/** True for a reconciled alternative-pool row (its `members` describe the products). */
export const isVariancePoolRow = (row: VarianceRow): boolean => row.pool !== undefined;

/**
 * One product inside a reconciled alternative pool. Quantities are in the MEMBER's own
 * std unit and are only comparable across members when `pool.quantitiesComparable` —
 * otherwise read the € fields only.
 */
export interface VariancePoolMember {
  key: string;
  productName: string;
  /** Raw platform + unknown-supplier name, so the UI can render a supplier logo. */
  platform: string;
  unknownSupplierName?: string;
  articleId?: string;
  /** The pool's anchor: a product some recipe lists as the primary (options[0]). */
  isPrimary: boolean;
  /** True when the product appears in at least one of the two counts. */
  counted: boolean;
  /**
   * Configured in a recipe but never counted, never bought and given no demand — it
   * moved nothing, so it neither clouds the reconciliation nor counts as an uncertainty
   * (render it muted). Every uncounted member that is NOT inert is in `membersUncounted`.
   */
  inert: boolean;
  /** Frozen counts, null when the member was never counted — NEVER assume 0 stock. */
  startQty: number | null;
  endQty: number | null;
  startValue: number | null;
  /** € of `endQty` — the member's slice of the row's `countedValue`. */
  countedValue: number | null;
  /** Window purchases in this member's own unit space (counted members included). */
  purchasedQty: number;
  purchasedValue: number | null;
  /**
   * Demand the allocation engine gave this member — an ESTIMATE (supply-proportional
   * split), never an observation. `allocatedIsEstimate` is always true inside a pool;
   * it exists so the UI can mark the number without re-deriving why.
   */
  allocatedNeededQty: number;
  allocatedNeededValue: number | null;
  allocatedIsEstimate: boolean;
  /** True when this member received demand as a NON-primary option (engine flag). */
  receivedViaAlternative: boolean;
  stdUnit: string;
  pricePerStdUnit: number | null;
  /** Display basis for the count-unit view — see `VarianceRow.displayCountUnit`. Each member
   *  measures its own stock, so each resolves its own basis. */
  displayCountUnit: string | null;
  displayStdPerCountUnit: number | null;
}

/** Pool metadata carried by a reconciled pool row. */
export interface VariancePoolInfo {
  /** Canonical pool id (lexicographically smallest member key) — stable across periods. */
  id: string;
  /** Member composition identity: sorted member keys joined with `|`. */
  signature: string;
  /** Configured members of the pool (counted, uncounted and excluded alike). */
  memberCount: number;
  /**
   * Names of members that were NOT counted yet DID contribute (purchases and/or
   * allocated demand). Their stock is unknown while their purchases are in — the
   * asymmetry that forces `confidence: 'low'`. Members that contributed nothing at
   * all are inert and deliberately not listed.
   */
  membersUncounted: string[];
  /**
   * Names of members dropped by the coverage guard: none of their stock or purchases is
   * reconciled here. Their opening stock and purchases DO still steer the allocation
   * supply, deliberately — withholding them would push their share of the demand onto a
   * sibling and manufacture leakage on that member instead.
   */
  membersExcluded: string[];
  /**
   * Names of members counted in ANOTHER inventory group. That group's own pool row
   * reconciles them over its own window, so nothing of theirs is summed here — this is
   * how a pool spanning two groups is counted once rather than twice in the all-groups
   * view. Same signal as the row-level `alsoInGroups`. Optional so existing callers and
   * fixtures keep compiling; the engine always sets it (empty when the pool is local).
   */
  membersInOtherGroups?: string[];
  /** Names of members whose opening stock could not enter the allocation supply (unit space). */
  membersOpeningStockBlocked: string[];
  /** Std unit shared by every contributing member, null when they disagree. */
  stdUnit: string | null;
  /**
   * False when this line has no quantity space at all — either the members mix std units,
   * or one of them is itself `countQtyUnreadable` (its own two counts were frozen in
   * unbridgeable spaces). EVERY quantity field on the row is then null and only € is real.
   */
  quantitiesComparable: boolean;
}

/** A reconciled product row (std units), valued + attributed + flagged. */
export interface VarianceRow {
  key: string;
  productName: string;
  /** Cost-price dishes that use this product (drawer "used in"). */
  dishes: string[];
  stdUnit: string;
  countUnit: string;
  /**
   * The unit this row's quantities can be RE-EXPRESSED in, and how. `countUnit` above
   * already existed and stays the FROZEN unit of the counts; these two are the resolved
   * display basis:
   *
   *   displayQty = stdQty / displayStdPerCountUnit
   *
   * Both null when nothing resolved — the renderer then keeps std units rather than dashing,
   * because a dash would hide real leakage. Never derive this ratio anywhere else: a wrong
   * factor prints plausible stock nobody can eyeball.
   */
  displayCountUnit: string | null;
  displayStdPerCountUnit: number | null;
  /**
   * Std-unit quantities. RUNTIME GUARANTEE: a product row (`pool === undefined`) whose two
   * counts were frozen in the SAME unit space always carries real numbers here, exactly as
   * before pooling existed — which is every product row in any report where no unit drifted.
   *
   * Null appears on exactly two kinds of row, both of which reconcile in € alone and must
   * render "—":
   *  - a MIXED-UNIT pool row (`pool.quantitiesComparable === false`), whose members measure
   *    in different units, so no single quantity exists for the pool;
   *  - ANY row flagged `countQtyUnreadable`, whose own two counts were frozen in unit spaces
   *    nothing can bridge, so no single quantity exists for the product.
   *
   * Both are declared by a flag as well as by the null, and `hasComparableQty` in
   * `leakagePoolDisplay.ts` is the one guard every renderer goes through. Pinned by
   * `inventoryVariance.test.ts`.
   */
  startQty: number | null;
  purchasedQty: number | null;
  neededQty: number | null;
  expectedQty: number | null; // start + purchased − needed
  endQty: number | null;
  varianceQty: number | null; // end − expected (negative = leakage)
  /** €/std-unit used for valuation (price chain). Null = no usable price. */
  pricePerStdUnit: number | null;
  startValue: number | null;
  purchasedValue: number | null;
  neededValue: number | null;
  expectedValue: number | null;
  countedValue: number | null;
  varianceValue: number | null;
  /** varianceValue / expectedValue, null when expected is 0. */
  variancePct: number | null;
  sectionChanges: VarianceSectionChange[];
  /** Section this row's whole variance attributes to (rules 1/2), null = unattributed. */
  attributedSectionKey: string | null;
  attributedSectionName: string | null;
  /** In ≥2 sections with offsetting deltas — movement contributes 0 variance. */
  moved: boolean;
  /** ≥2 sections changed → rule 3, variance lands in the unattributed row. */
  mixedSections: boolean;
  hasUnmeasurablePurchases: boolean;
  noRecipeCoverage: boolean; // neededQty === 0
  /**
   * The recipes hold this product in a DIFFERENT unit space than the count froze it in
   * (`neededQtyForCount`): the usage was either converted through the recipe snapshot's
   * pack size, or — when no conversion exists — dropped to 0 rather than subtracting
   * millilitres from a count of bottles. Either way the product's unit setup is stale and
   * the row says so instead of printing a fabricated usage.
   *
   * OPTIONAL and set only when true, so a report with no drift serialises exactly as it
   * did before — pinned by `fixtures/varianceNoAlternatives.json`.
   */
  neededUnitMismatch?: boolean;
  /**
   * Base units of the RECIPE's space per one unit of this row's space, when the guard
   * above converted the usage (e.g. 3000 for a 3 L bottle counted per piece). Anything
   * still rendering a raw recipe quantity for this row — the drawer's zero-sold setup
   * cards — must divide by it, or it prints "3000 pcs per dish". Absent when nothing was
   * converted, including when no conversion existed.
   */
  neededUnitFactor?: number;
  /**
   * The two COUNTS themselves were frozen in different std-unit spaces — the opening and
   * the closing session, or two sections of one session (`countSpaceBridge`). The
   * quantities on this row were rebased onto the END count's space through the pack size
   * the frozen items state, which is exact, but the product's unit setup is stale and the
   * row says so instead of quietly reporting a rebased number as if nothing happened.
   *
   * OPTIONAL and set only when true, so a report with no drift serialises exactly as it
   * did before — pinned by `fixtures/varianceNoAlternatives.json`.
   */
  countUnitMismatch?: boolean;
  /**
   * …and NOTHING could bridge those spaces, so this product has no quantity in any single
   * space: every quantity field above is null and the row reconciles in € alone, off each
   * session's own frozen value. Set together with `countUnitMismatch`, never on its own.
   *
   * NOT the same as "the shelf is empty" — see the `startQty` contract. A pooled row
   * carrying such a member drops out of quantity space with it.
   */
  countQtyUnreadable?: boolean;
  /** Product key also held in another group's most recent session. */
  alsoInGroups: string[];
  negativeExpected: boolean; // expectedQty < 0 → count/recipe data error
  /**
   * Stock for this product came ONLY from exploded subrecipe counts — it is not counted
   * as loose stock anywhere, so purchases have no shelf to land on and the variance
   * systematically over-reads as leakage (hence a forced `confidence: 'low'`, and no
   * relink action in the drawer: there is no assignment for this key in any section).
   *
   * OPTIONAL and set only when true, so a report without preps serialises exactly as it
   * did before — pinned by `fixtures/varianceNoAlternatives.json`.
   */
  onlyFromSubrecipe?: boolean;
  inventoryCategoryName: string | null;
  confidence: VarianceConfidence;
  status: VarianceStatus;
  /**
   * Set ONLY on a reconciled alternative-pool row (`key` = `pool:<id>`), which replaces
   * the individual rows of its counted members: primary and alternatives are one
   * interchangeable supply, so only their sum reconciles. Undefined on product rows.
   */
  pool?: VariancePoolInfo;
  /** The pool's per-product facts (pooled rows only) — see `VariancePoolMember`. */
  members?: VariancePoolMember[];
}

/** Aggregated variance per section (plus a synthetic unattributed row). */
export interface VarianceSectionRow {
  sectionKey: string;
  sectionName: string;
  deleted: boolean;
  expectedValue: number;
  countedValue: number;
  varianceValue: number;
  /** variance / expected, null when expected is 0. */
  variancePct: number | null;
  /** Σ per-section deltaValue — the raw movement (layer-1 fact). */
  changeValue: number;
}

/** Category aggregate — both `inventory` (frozen cat) and `product` (live cat) modes. */
export interface VarianceCategoryRow {
  name: string;
  mode: 'inventory' | 'product';
  expectedValue: number;
  countedValue: number;
  varianceValue: number;
}

/** A product without an opening count, excluded from leakage. */
export interface VarianceExcludedProduct {
  key: string;
  name: string;
  sections: string[];
  startValue: number;
  endValue: number;
}

export interface VarianceReportData {
  hasData: boolean;
  window: ConsumptionWindowInfo | null;
  /** Reconciled rows, sorted by varianceValue ascending (biggest leakage first). */
  rows: VarianceRow[];
  customItems: ConsumptionCustomRow[];
  /** Per-section aggregates + the synthetic unattributed row (last). */
  sections: VarianceSectionRow[];
  categories: VarianceCategoryRow[];
  excluded: VarianceExcludedProduct[];
  /** countedTotalValue − varianceTotalValue (non-variance products assumed unchanged). */
  expectedTotalValue: number;
  /** Frozen total of the end count (matches the Count Review page). */
  countedTotalValue: number;
  varianceTotalValue: number;
  purchasedTotalValue: number;
  neededTotalValue: number;
  /** Frozen total of the baseline count (matches the Count Review page). */
  startTotalValue: number;
  /**
   * expected − (start + purchases − usage): the change of products assumed
   * unchanged (custom / not linked / coverage-excluded) plus price revaluation
   * between the two counts. Makes the reconciliation row an exact identity.
   */
  adjustmentValue: number;
  highRiskCount: number;
  /** linkedProductCount / totalProductCount (0..1). */
  confidenceScore: number;
  /** End-count products with a usable price (non-custom). */
  linkedProductCount: number;
  /** All end-count products. */
  totalProductCount: number;
  unattributedVarianceValue: number;
  /** |unattributed| / Σ|product variance|, 0 when there's no variance. */
  unattributedShare: number;
  unmeasurableProductNames: string[];
}

/**
 * One product's STARRED (primary) count unit, taken from its inventory group's canonical
 * assignment (`getGroupCanonicalAssignments`, exposed by `/groups/:id/all-products/:rid`).
 * Not the frozen unit of whatever a counter happened to type — one unit per product per group.
 */
export interface CanonicalCountUnit {
  countUnit: string;
  /** Std units per ONE counted unit (`count_unit_to_std_factor`). Always > 0. */
  stdPerCountUnit: number;
  /** The assignment's std unit — the row's own std unit must match, or the factor is refused. */
  stdUnit: string;
}

export interface VarianceReportInputs {
  countsHistory: InventoryCount[];
  /** All-time order history (window-filtered here; full list for latest price). */
  orders: OrderHistory[];
  /** All-time revenue history (window-filtered here for the needed-index). */
  revenue: RevenueHistory[];
  costPriceList: CostPriceAnalysis[];
  subrecipesById: Map<string, Subrecipe>;
  defaultWastePercentage: number;
  currentRestaurantId: string;
  selection: VarianceSelection;
  /** p6 supplies the live fine-category label per inventory key (product mode). */
  liveCategoryByKey?: Map<string, string>;
  /**
   * groupKey → (BASE product key → starred count unit). Optional: absent = the page never
   * turned the count-unit view on, and every row falls back to its own frozen space.
   * Keyed by `groupKey` because that is what `groupKeyOf` produces and what
   * `computeInventoryVarianceAll` iterates.
   */
  canonicalCountUnits?: Map<string, Map<string, CanonicalCountUnit>>;
}

const EMPTY: VarianceReportData = {
  hasData: false,
  window: null,
  rows: [],
  customItems: [],
  sections: [],
  categories: [],
  excluded: [],
  expectedTotalValue: 0,
  countedTotalValue: 0,
  varianceTotalValue: 0,
  purchasedTotalValue: 0,
  neededTotalValue: 0,
  startTotalValue: 0,
  adjustmentValue: 0,
  highRiskCount: 0,
  confidenceScore: 0,
  linkedProductCount: 0,
  totalProductCount: 0,
  unattributedVarianceValue: 0,
  unattributedShare: 0,
  unmeasurableProductNames: [],
};

// --- helpers --------------------------------------------------------------

/** Stable bucket for "one physical group". Legacy/ungrouped counts collapse together. */
function groupKeyOf(session: SessionGroup): string {
  return session.groupId ?? session.groupName ?? 'ungrouped';
}

/** Frozen inventory-category name, read defensively (INVCOUNT p1 freezes it on items). */
function frozenCategoryName(item: InventoryCountItem): string | null {
  return (item as InventoryCountItem & { inventoryCategoryName?: string | null }).inventoryCategoryName ?? null;
}

interface ProductAgg extends ExplodableProductAgg {
  bySection: Map<string, number>;
  /**
   * Frozen € per section. The section layer's only reconcilable space when the two counts
   * disagree about units — a frozen value is a fact OF its own count, whatever unit that
   * count was taken in, while the quantities are not comparable at all.
   */
  valueBySection: Map<string, number>;
  countUnit: string;
  /** Std units per one counted unit, backed out of the frozen items — see `FrozenCountSpace`. */
  stdPerCountUnit: number | null;
  /** Some item summed here was frozen in another space than this aggregate's. */
  unitMismatch: boolean;
  /** …and nothing could convert it, so `totalQty` no longer covers every item. */
  qtyUnreadable: boolean;
  price: number | null;
  value: number; // frozen value sum
  inventoryCategoryName: string | null;
}

/** The unit basis an aggregate is currently expressed in. */
function spaceOf(agg: ProductAgg): FrozenCountSpace {
  return { stdUnit: agg.stdUnit, countUnit: agg.countUnit, stdPerCountUnit: agg.stdPerCountUnit };
}

// --- count-unit display basis ---------------------------------------------

/** The `displayCountUnit` / `displayStdPerCountUnit` pair a row or member carries. */
interface DisplayBasis {
  displayCountUnit: string | null;
  displayStdPerCountUnit: number | null;
}

/** Nothing resolved — the renderer keeps std units (never a dash: that hides leakage). */
const NO_DISPLAY_BASIS: DisplayBasis = { displayCountUnit: null, displayStdPerCountUnit: null };

/**
 * The space the row's own counts froze this product in — the END count's, since that is the
 * space the row reports. A zero count states no factor (0/0), and the baseline may only fill
 * that gap when it counted the SAME physical unit in the SAME std space; anything else is a
 * different physical basis, not a fallback.
 */
function frozenBasisOf(end: ProductAgg | undefined, base: ProductAgg | undefined): FrozenCountSpace | null {
  if (!end) return base ? spaceOf(base) : null;
  if (end.stdPerCountUnit !== null) return spaceOf(end);
  if (base && base.countUnit === end.countUnit && base.stdUnit === end.stdUnit) return spaceOf(base);
  return spaceOf(end);
}

/**
 * Is this basis a NO-OP — a "count unit" that is just the std unit's own label?
 *
 * `defaultsForStdUnit` hands piece products `('p', 1)` and unmeasured ones `('unit', 1)`, so
 * most products resolve a basis that re-expresses nothing. Converting through it would swap
 * the base formatter for the count formatter and DOWNGRADE the output for no gain —
 * "12,000 pcs" becomes "12000.00 p" on the PDF. Nothing is being expressed in a different
 * physical unit, so the row keeps std formatting.
 */
function isNoOpBasis(countUnit: string, stdUnit: string): boolean {
  return countUnit.trim().toLowerCase() === stdUnit.trim().toLowerCase();
}

/**
 * Resolve the unit a row's quantities may be re-expressed in: the group's STARRED assignment
 * first, then the space the counts themselves were frozen in, then nothing.
 *
 * The std-unit guard is the same refusal `countSpaceBridge` makes — a factor stated for
 * another std space would rescale the row (the 3 L bottle that reads as 3000 pieces), so a
 * mismatch falls through instead of inventing a conversion.
 */
function resolveDisplayBasis(
  canonical: CanonicalCountUnit | undefined,
  stdUnit: string,
  frozen: FrozenCountSpace | null
): DisplayBasis {
  if (
    canonical &&
    canonical.countUnit &&
    canonical.stdPerCountUnit > 0 &&
    canonical.stdUnit === stdUnit &&
    !isNoOpBasis(canonical.countUnit, stdUnit)
  ) {
    return { displayCountUnit: canonical.countUnit, displayStdPerCountUnit: canonical.stdPerCountUnit };
  }
  if (
    frozen &&
    frozen.countUnit &&
    frozen.stdPerCountUnit &&
    frozen.stdPerCountUnit > 0 &&
    frozen.stdUnit === stdUnit &&
    !isNoOpBasis(frozen.countUnit, stdUnit)
  ) {
    return { displayCountUnit: frozen.countUnit, displayStdPerCountUnit: frozen.stdPerCountUnit };
  }
  return NO_DISPLAY_BASIS;
}

interface SectionInfo {
  name: string;
  deleted: boolean;
  productKeys: Set<string>;
  value: number;
}

interface SessionAgg {
  perProduct: Map<string, ProductAgg>;
  sectionInfo: Map<string, SectionInfo>;
}

/** Section key for a count: real section id, or `deleted:<snapshot>` for removed sections. */
function sectionKeyOf(c: InventoryCount): string {
  return c.inventorySectionId ?? `deleted:${c.sectionNameSnapshot}`;
}

/** Aggregate a session's counts into per-product (per-section) sums + per-section facts.
 *  Products are keyed by BASE key so variant rows (`…__v<n>`) recombine with their base.
 *
 *  Sections are counted independently and each carries its own assignment, so ONE session
 *  can hold the same product in two different frozen unit spaces. Items that disagree are
 *  converted through the pack size the frozen items themselves state, and refused when
 *  nothing can convert them (`foldIntoCountSpace`) — adding them straight through is what
 *  turns a 3 L bottle into 3000 of something. */
function aggregateSession(session: SessionGroup): SessionAgg {
  const perProduct = new Map<string, ProductAgg>();
  const sectionInfo = new Map<string, SectionInfo>();

  for (const c of session.counts) {
    const sk = sectionKeyOf(c);
    let sec = sectionInfo.get(sk);
    if (!sec) {
      sec = { name: c.sectionNameSnapshot, deleted: !c.inventorySectionId, productKeys: new Set(), value: 0 };
      sectionInfo.set(sk, sec);
    }
    for (const item of c.items) {
      const key = baseProductKey(item.productKey);
      sec.productKeys.add(key);
      sec.value += item.value;

      const space = frozenCountSpace(item);
      const existing = perProduct.get(key);
      if (existing) {
        const fold = foldIntoCountSpace(
          { totalQty: existing.totalQty, space: spaceOf(existing) },
          { qty: item.countInStdUnit, space }
        );
        if (fold.retarget) {
          existing.stdUnit = fold.retarget.stdUnit;
          existing.countUnit = fold.retarget.countUnit;
          existing.stdPerCountUnit = fold.retarget.stdPerCountUnit;
        }
        if (fold.mismatch) existing.unitMismatch = true;
        if (fold.qty === null) {
          existing.qtyUnreadable = true;
          // The section still HOLDS this product — register it (at no quantity, which the
          // row loop never reads on an unreadable key) so the coverage guard and the
          // section layer still see it.
          existing.bySection.set(sk, existing.bySection.get(sk) ?? 0);
        } else {
          existing.totalQty += fold.qty;
          existing.bySection.set(sk, (existing.bySection.get(sk) ?? 0) + fold.qty);
        }
        existing.value += item.value;
        existing.valueBySection.set(sk, (existing.valueBySection.get(sk) ?? 0) + item.value);
        // A first item that could not state a factor (a zero count) leaves the aggregate
        // unable to bridge anything later — take the first one in the same space that can.
        if (existing.stdPerCountUnit === null && space.stdUnit === existing.stdUnit && space.countUnit === existing.countUnit) {
          existing.stdPerCountUnit = space.stdPerCountUnit;
        }
        if (item.pricePerStdUnit) existing.price = item.pricePerStdUnit;
        if (existing.inventoryCategoryName == null) existing.inventoryCategoryName = frozenCategoryName(item);
      } else {
        perProduct.set(key, {
          totalQty: item.countInStdUnit,
          bySection: new Map([[sk, item.countInStdUnit]]),
          valueBySection: new Map([[sk, item.value]]),
          name: item.productName,
          stdUnit: space.stdUnit,
          countUnit: space.countUnit,
          stdPerCountUnit: space.stdPerCountUnit,
          unitMismatch: false,
          qtyUnreadable: false,
          price: item.pricePerStdUnit || null,
          value: item.value,
          inventoryCategoryName: frozenCategoryName(item),
        });
      }
    }
  }

  return { perProduct, sectionInfo };
}

/**
 * May a key's frozen OPENING count be added to the base-unit allocation supply?
 *
 * Counts are frozen in their own std unit while the supply map is in the order lines'
 * base units, so the two only add up when they live in the same space: a count frozen
 * as `'unit'` is denominated in whole ordered packages (see `purchasedQtyForCount`) and
 * can never be added to grams, and a window whose purchase lines disagree on the unit
 * (`stdUnit === null` with volume) tells us nothing. In those cases the key keeps
 * purchases-only supply and is flagged (`pool.membersOpeningStockBlocked`).
 */
export function openingStockFitsSupply(countStdUnit: string, buy: WindowPurchase | undefined): boolean {
  if (!countStdUnit || countStdUnit === 'unit') return false;
  // No measurable purchase line in the window → nothing can contradict the count's unit.
  if (!buy || (buy.baseQty === 0 && !buy.stdUnit)) return true;
  return buy.stdUnit === countStdUnit;
}

// --- counted subrecipes (preps) -------------------------------------------

/**
 * The slice of a per-product aggregate the prep explosion reads and writes. Both this
 * module's `ProductAgg` and `estimatedInventory.ts`'s count aggregate satisfy it, so the
 * two pages explode preps through ONE implementation (and inherit one unit guard).
 */
export interface ExplodableProductAgg {
  totalQty: number;
  name: string;
  stdUnit: string;
  /** Per-section quantities. Omitted by callers with no section layer (estimated stock). */
  bySection?: Map<string, number>;
  /**
   * Stock for this product came ONLY from exploded prep counts — it is not counted as
   * loose stock anywhere, so purchases have no shelf to land on.
   */
  onlyFromSubrecipe?: boolean;
}

/** What the explosion needs beyond the counts themselves. */
export interface SubrecipeExplosionContext {
  subrecipesById: Map<string, Subrecipe>;
  defaultWastePercentage: number;
  /** Part of the shared `ExplodeDishOptions` shape; the subrecipe walk never reads it. */
  currentRestaurantId: string;
}

/**
 * Replace every counted prep in `perProduct` with the raw ingredient stock it holds.
 *
 * `needed` already charges each raw ingredient at SALE time by walking dish → prep →
 * leaf, but the raw stock physically leaves the shelf at PREP time. Without this, a big
 * batch prepped just before a closing count reads as leakage on every ingredient in it.
 * 10 L of a stock that yields 20 L from 4 kg of carrots means 2 kg of carrots are still
 * on hand — in the stockpot, but on hand.
 *
 * Runs over the aggregated session before product eligibility and pool allocation.
 *
 * Rules, in order:
 *  1. Include counted preps from every section, regardless of the other session.
 *  2. Counted portions = the frozen `countInStdUnit` (portion-units of the prep).
 *  3. Leaf quantity = portions × per-portion-unit base, waste % already applied.
 *  4. A leaf whose harvested std unit contradicts the entry already in the map is SKIPPED
 *     — snapshot vs live std-unit drift is a pre-existing hazard, and adding across unit
 *     spaces would silently produce "5 packages + 12 pieces".
 *  5. The quantity lands on `totalQty` and on the PREP's section.
 *  6. `value` is NEVER touched: the prep's own frozen value already stands for this stock
 *     in the section and session totals, and counting it twice would move the headline.
 *  7. The prep key is dropped, so it never becomes a product row of its own.
 *  8. A leaf nothing counted loose is created at `value: 0` and flagged `onlyFromSubrecipe`.
 *
 * `createLeaf` builds an empty entry in the caller's own shape (rule 8); the quantity and
 * the flag are filled in here.
 */
export function applySubrecipeExplosion<T extends ExplodableProductAgg>(
  perProduct: Map<string, T>,
  ctx: SubrecipeExplosionContext,
  createLeaf: (identity: { name: string; stdUnit: string }) => T
): void {
  const prepKeys = Array.from(perProduct.keys()).filter((key) => key.startsWith('subrecipe:'));

  for (const prepKey of prepKeys) {
    const prep = perProduct.get(prepKey) as T;
    perProduct.delete(prepKey); // rule 7 — unconditional, even when nothing explodes
    const sub = ctx.subrecipesById.get(prepKey.slice('subrecipe:'.length));
    // A deleted subrecipe keeps its frozen count row (like any deleted product) but has
    // no recipe left to explode.
    if (!sub) continue;

    const needs = explodeSubrecipeToProductNeeds(sub, ctx.subrecipesById, {
      defaultWastePercentage: ctx.defaultWastePercentage,
      restaurantId: ctx.currentRestaurantId,
      // The default keyer emits canonical keys, which match nothing in inventory space.
      keyOf: inventoryKey,
    });
    if (needs.length === 0) continue;

    // Callers without a section layer explode the whole counted amount at once.
    const bySection = prep.bySection ?? new Map<string, number>([['', prep.totalQty]]);
    for (const [sectionKey, portions] of Array.from(bySection.entries())) {
      if (!portions) continue; // rule 2
      for (const need of needs) {
        const base = portions * need.perDishBase; // rule 3
        if (!base) continue;
        const existing = perProduct.get(need.productKey);
        if (existing) {
          if (existing.stdUnit !== need.stdUnit) {
            // rule 4
            console.warn(
              `Subrecipe "${sub.name}" holds ${need.productName} in '${need.stdUnit}' but the count froze '${existing.stdUnit}' — prep stock not added`
            );
            continue;
          }
          existing.totalQty += base; // rule 5
          existing.bySection?.set(sectionKey, (existing.bySection.get(sectionKey) ?? 0) + base);
          continue;
        }
        const created = createLeaf({ name: need.productName, stdUnit: need.stdUnit }); // rule 8
        created.totalQty = base;
        created.onlyFromSubrecipe = true;
        created.bySection?.set(sectionKey, base);
        perProduct.set(need.productKey, created);
      }
    }
  }
}

/** Resolve a group's baseline options: sessions strictly older than `endSessionKey`, newest-first. */
export function buildVarianceBaselineOptions(
  countsHistory: InventoryCount[],
  groupKey: string,
  endSessionKey: string
): ConsumptionPoint[] {
  const sessions = groupCountsIntoSessions(countsHistory).filter((s) => groupKeyOf(s) === groupKey);
  const end = sessions.find((s) => s.key === endSessionKey);
  if (!end) return [];
  const endAt = end.completedAt.getTime();
  return sessions
    .filter((s) => s.completedAt.getTime() < endAt)
    .map((s) => ({
      key: s.key,
      label: getTableFormattedDate(s.completedAt),
      completedAt: s.completedAt.getTime(),
      totalValue: s.totalValue,
    }));
}

/** Shared row classification: leakage size vs the OK band, once confidence allows it. */
function classifyVarianceStatus(
  confidence: VarianceConfidence,
  varianceValue: number | null,
  expectedValue: number | null
): VarianceStatus {
  if (confidence === 'low' || varianceValue === null) return 'lowConfidence';
  const threshold = Math.max(VARIANCE_OK_EUR, VARIANCE_OK_PCT * Math.abs(expectedValue ?? 0));
  if (Math.abs(varianceValue) <= threshold) return 'ok';
  if (varianceValue <= -VARIANCE_HIGH_RISK_EUR) return 'highRisk';
  if (varianceValue > 0) return 'overCounted';
  return 'review';
}

// --- alternative-pool reconciliation --------------------------------------

interface PooledRowContext {
  /** Canonical pool id → sorted member keys (`collectPools`, ≥2 members). */
  pools: Map<string, string[]>;
  /** The product rows already built for every counted, non-excluded key. */
  rowsByKey: Map<string, VarianceRow>;
  /** Keys dropped by the coverage guard — out of the reconciliation entirely. */
  excludedKeys: Set<string>;
  purchases: Map<string, WindowPurchase>;
  /** Post-allocation needs per key (`base` = allocated demand). */
  needed: Map<string, NeededInfo>;
  /** Keys a recipe lists as its primary option — the pool's anchor candidates. */
  primaryKeys: Set<string>;
  openingBlockedKeys: Set<string>;
  /** Display info from the recipe rows, for members never bought and never counted. */
  needInfoByKey: Map<string, NeedProductInfo>;
  latestPrice: (key: string) => number | null;
  /** Key → OTHER groups holding it in their latest session — the cross-group signal. */
  alsoInGroupsByKey: Map<string, Set<string>>;
  /** This group's starred count units, when the page asked for the count-unit view. */
  canonicalUnits?: Map<string, CanonicalCountUnit>;
}

/**
 * Reconcile each interchangeable-supply pool as ONE row.
 *
 * A primary and its alternatives are one pool of stock the kitchen draws from, and the
 * allocator's per-member split is an estimate — only the SUM is an observation. So a
 * pool with at least one counted member yields a single row whose needs are the summed
 * allocation, whose counts sum the COUNTED members only, and whose purchases sum every
 * member it covers (a never-counted member's purchases really did enter the kitchen; its
 * stock is simply unknown, and is never assumed to be zero). That asymmetry is what
 * `pool.membersUncounted` + `confidence: 'low'` exist to declare.
 *
 * A pool whose members are counted in DIFFERENT inventory groups is reconciled once per
 * group, each row covering only the members counted in ITS group, because the two groups
 * are compared over different windows and a member's stock belongs to the inventory it
 * sits in. Members counted elsewhere are named in `pool.membersInOtherGroups` instead of
 * being summed twice; members nobody counted are claimed by exactly one group. So across
 * the all-groups view every member's stock, purchases and demand are counted once.
 *
 * Members that mix std units cannot be added in quantity space, so those rows reconcile
 * in € only (each member's quantities priced at its own €/std-unit) — the same
 * mixed-unit convention as the utilization page's group rows.
 */
function buildPooledVarianceRows(ctx: PooledRowContext): VarianceRow[] {
  const out: VarianceRow[] = [];

  for (const [poolId, memberKeys] of Array.from(ctx.pools.entries())) {
    if (memberKeys.length < 2) continue; // a lone product is not a pool
    if (!memberKeys.some((k) => ctx.rowsByKey.has(k))) continue; // nothing counted → not inventory

    // Cross-group ownership, decided before any member is summed. Every group's run
    // evaluates these identically from the same counts, so a member is claimed once.
    const countedHere = (k: string): boolean => ctx.rowsByKey.has(k);
    const countedElsewhere = (k: string): boolean => (ctx.alsoInGroupsByKey.get(k)?.size ?? 0) > 0;
    const countedSomewhere = (k: string): boolean => countedHere(k) || countedElsewhere(k);
    // A member nobody counted still has to be reconciled exactly once: it belongs to the
    // group holding the pool's primary, else the group holding its smallest counted key.
    const orphanOwner =
      memberKeys.find((k) => ctx.primaryKeys.has(k) && countedSomewhere(k)) ?? memberKeys.find(countedSomewhere);
    const ownsOrphans = orphanOwner !== undefined && countedHere(orphanOwner);

    const members: VariancePoolMember[] = [];
    const membersExcluded: string[] = [];
    const membersInOtherGroups: string[] = [];
    /** Members whose window purchases could not be read in their own unit space. */
    const unmeasurableKeys = new Set<string>();
    /** Members whose recipe quantities live in a different unit space than their count. */
    const needMismatchKeys = new Set<string>();
    /** …and the subset no conversion existed for, whose demand is missing from the sums. */
    const needDroppedKeys = new Set<string>();
    /** Members whose OWN two counts were frozen in different unit spaces. */
    const countMismatchKeys = new Set<string>();
    /** …and the subset nothing could bridge, which have no quantity to sum at all. */
    const countUnreadableKeys = new Set<string>();
    for (const key of memberKeys) {
      const row = ctx.rowsByKey.get(key);
      const buy = ctx.purchases.get(key);
      const info = ctx.needInfoByKey.get(key);
      const memberName = row?.productName || buy?.name || info?.name || key;
      if (!row && ctx.excludedKeys.has(key)) {
        // The coverage guard dropped this product, so none of its stock or purchases is
        // reconciled here. Its opening stock and purchases DO still steer the allocation
        // supply — leaving them out would hand its share of the demand to a sibling and
        // manufacture leakage there, which is worse than the row being one member short.
        membersExcluded.push(memberName);
        continue;
      }
      if (!row) {
        // Counted in another group: that group's row reconciles its stock over its own
        // window. Naming it keeps the pool's composition visible without summing twice.
        if (countedElsewhere(key)) {
          membersInOtherGroups.push(memberName);
          continue;
        }
        if (!ownsOrphans) continue; // a never-counted member: another group claims it
      }
      const need = ctx.needed.get(key);
      const stdUnit = row?.stdUnit || buy?.stdUnit || info?.stdUnit || '';
      const price = row ? row.pricePerStdUnit : ctx.latestPrice(key);
      // Counted members already resolved their purchases against the frozen count unit;
      // an uncounted member resolves against whatever unit we could name it in.
      const bought = purchasedQtyForCount(buy, stdUnit);
      // A member row that dropped out of quantity space sends null for its purchases too,
      // yet those purchases ARE readable in the row's own std unit — recompute rather than
      // fold a real delivery in as 0 and understate the pool's expected stock.
      const purchasedQty = row ? row.purchasedQty ?? bought.qty : bought.qty;
      if (row ? row.hasUnmeasurablePurchases : bought.unitMismatch || !!buy?.hadUnmeasurable) {
        unmeasurableKeys.add(key);
      }
      if (row?.countUnitMismatch) countMismatchKeys.add(key);
      if (row?.countQtyUnreadable) countUnreadableKeys.add(key);
      // Same unit guard the product rows apply: the allocated demand is in the RECIPE's
      // snapshot space, the member's quantities in its count's.
      const allocated = neededQtyForCount(need?.base ?? 0, info, stdUnit);
      const allocatedNeededQty = allocated.qty;
      if (allocated.unitMismatch) {
        needMismatchKeys.add(key);
        if (allocated.qty === 0) needDroppedKeys.add(key);
      }
      const valueOf = (q: number | null): number | null => (price !== null && q !== null ? q * price : null);
      members.push({
        key,
        productName: memberName,
        // Every pool member comes from a recipe row, so the platform is known; an
        // inventory key can NEVER be parsed back for it (identifiers contain '-' too).
        // Empty means "unknown supplier" — the UI skips the logo rather than guessing.
        platform: info?.platform ?? '',
        unknownSupplierName: info?.customPlatformName,
        articleId: info?.articleId,
        isPrimary: false, // the anchor is picked below, once every member is known
        counted: !!row,
        // ANY nonzero purchase makes a member contribute — a credit note nets negative
        // (see dataRules/orderhistory.md), and folding that in unflagged would understate
        // the leakage it belongs to.
        inert: !row && purchasedQty === 0 && allocatedNeededQty <= 0,
        startQty: row ? row.startQty : null,
        endQty: row ? row.endQty : null,
        startValue: row ? row.startValue : null,
        countedValue: row ? row.countedValue : null,
        purchasedQty,
        purchasedValue: valueOf(purchasedQty),
        allocatedNeededQty,
        allocatedNeededValue: valueOf(allocatedNeededQty),
        allocatedIsEstimate: true,
        receivedViaAlternative: need?.receivedViaAlternative ?? false,
        stdUnit,
        pricePerStdUnit: price,
        // A counted member already resolved its basis on its own row; an uncounted one has
        // no frozen space at all, so only the group's starred assignment can name its unit.
        ...(row
          ? { displayCountUnit: row.displayCountUnit, displayStdPerCountUnit: row.displayStdPerCountUnit }
          : resolveDisplayBasis(ctx.canonicalUnits?.get(key), stdUnit, null)),
      });
    }
    // Guaranteed non-empty by the guard above; kept as the invariant every read of
    // `countedRows[0]` below relies on.
    const countedRows = members.filter((m) => m.counted).map((m) => ctx.rowsByKey.get(m.key) as VarianceRow);
    if (countedRows.length === 0) continue;

    // Anchor = a counted product some recipe uses as its primary, else any primary,
    // else the first counted member (`memberKeys` is sorted → deterministic).
    const anchor =
      members.find((m) => m.counted && ctx.primaryKeys.has(m.key)) ??
      members.find((m) => ctx.primaryKeys.has(m.key)) ??
      members.find((m) => m.counted) ??
      members[0];
    anchor.isPrimary = true;

    // Inert members can neither agree nor disagree about units — they must not veto the
    // pool's quantity space.
    const contributing = members.filter((m) => !m.inert);
    const sharedUnit =
      contributing.length > 0 && contributing.every((m) => m.stdUnit !== '' && m.stdUnit === contributing[0].stdUnit)
        ? contributing[0].stdUnit
        : null;
    // A member whose own two counts were frozen in unbridgeable spaces has no quantity in
    // any space (its row already sends null), so the pool cannot be summed in quantity
    // space either — it falls back to the € reconciliation mixed-unit pools already use.
    const anyMemberQtyUnreadable = members.some((m) => countUnreadableKeys.has(m.key));
    const comparable = sharedUnit !== null && !anyMemberQtyUnreadable;

    // The pool's own quantities are a SUM across members, so ONE factor may re-express them
    // only when every member IN THAT SUM measures in the same physical unit at the same rate.
    // Anything else (a 'fles' primary beside a 'krat' alternative) has no single count unit,
    // and the row keeps std units — the same refusal a mixed std space already gets.
    //
    // Tested over `contributing`, NOT over the counted members: `sumQty` below reduces across
    // every member, so an uncounted-but-contributing sibling's purchases and allocated demand
    // are inside the totals. Judging the basis on the counted ones alone would label a sum
    // that includes 24 litres of bottles as "3.03 krat". A pool still needs one counted member
    // to be inventory at all, hence the `some(counted)` guard.
    const basisOwner = contributing[0];
    const poolBasis: DisplayBasis =
      comparable &&
      contributing.length > 0 &&
      contributing.some((m) => m.counted) &&
      basisOwner.displayCountUnit !== null &&
      contributing.every(
        (m) =>
          m.displayCountUnit === basisOwner.displayCountUnit &&
          m.displayStdPerCountUnit === basisOwner.displayStdPerCountUnit
      )
        ? {
            displayCountUnit: basisOwner.displayCountUnit,
            displayStdPerCountUnit: basisOwner.displayStdPerCountUnit,
          }
        : NO_DISPLAY_BASIS;

    // Mixed units have no quantity space at all — null, never 0, so nothing downstream
    // can mistake "cannot be expressed" for "nothing there".
    const sumQty = (pick: (m: VariancePoolMember) => number | null): number | null =>
      comparable ? members.reduce((s, m) => s + (pick(m) ?? 0), 0) : null;
    const startQty = sumQty((m) => m.startQty);
    const endQty = sumQty((m) => m.endQty);
    const purchasedQty = sumQty((m) => m.purchasedQty);
    const neededQty = sumQty((m) => m.allocatedNeededQty);
    const expectedQty = comparable ? (startQty as number) + (purchasedQty as number) - (neededQty as number) : null;
    const varianceQty = comparable ? (endQty as number) - (expectedQty as number) : null;

    // € always reconciles, even across units: every member prices its own quantities.
    const anyPriced = contributing.some((m) => m.pricePerStdUnit !== null);
    const sumValue = (pick: (m: VariancePoolMember) => number | null): number | null =>
      anyPriced ? members.reduce((s, m) => s + (pick(m) ?? 0), 0) : null;
    const startValue = sumValue((m) => m.startValue);
    const purchasedValue = sumValue((m) => m.purchasedValue);
    const neededValue = sumValue((m) => m.allocatedNeededValue);
    const countedValue = sumValue((m) => m.countedValue);
    const expectedValue =
      startValue !== null && purchasedValue !== null && neededValue !== null
        ? startValue + purchasedValue - neededValue
        : null;
    const varianceValue = countedValue !== null && expectedValue !== null ? countedValue - expectedValue : null;

    // --- section layer: counted members only, summed per section ---
    const bySection = new Map<string, VarianceSectionChange>();
    for (const m of members) {
      const row = ctx.rowsByKey.get(m.key);
      if (!row) continue;
      for (const sc of row.sectionChanges) {
        let agg = bySection.get(sc.sectionKey);
        if (!agg) {
          agg = {
            sectionKey: sc.sectionKey,
            sectionName: sc.sectionName,
            deleted: sc.deleted,
            startQty: 0,
            endQty: 0,
            deltaQty: 0,
            deltaValue: 0,
            endValue: 0,
          };
          bySection.set(sc.sectionKey, agg);
        }
        if (comparable) {
          agg.startQty += sc.startQty;
          agg.endQty += sc.endQty;
          agg.deltaQty += sc.deltaQty;
        }
        agg.deltaValue = (agg.deltaValue ?? 0) + (sc.deltaValue ?? 0);
        agg.endValue = (agg.endValue ?? 0) + (m.pricePerStdUnit !== null ? sc.endQty * m.pricePerStdUnit : 0);
      }
    }
    const sectionChanges = Array.from(bySection.values()).sort((a, b) => a.sectionName.localeCompare(b.sectionName));

    // Attribution: keep the section only when every counted member points at the same one.
    const attributedCandidates = new Set(countedRows.map((r) => r.attributedSectionKey));
    const attributedSectionKey =
      attributedCandidates.size === 1 && !attributedCandidates.has(null) ? countedRows[0].attributedSectionKey : null;
    const attributedSectionName = attributedSectionKey
      ? sectionChanges.find((c) => c.sectionKey === attributedSectionKey)?.sectionName ?? null
      : null;
    const mixedSections =
      attributedSectionKey === null && (sectionChanges.length >= 2 || countedRows.some((r) => r.mixedSections));
    // Movement is read in € here — it is the one space every pool shares.
    const netChange = sectionChanges.reduce((s, c) => s + (c.deltaValue ?? 0), 0);
    const absChange = sectionChanges.reduce((s, c) => s + Math.abs(c.deltaValue ?? 0), 0);
    const moved = sectionChanges.length >= 2 && absChange > Math.abs(netChange) + EPS;

    // --- flags / confidence / status ---
    // Only members that actually moved value create the counted/uncounted asymmetry;
    // a configured alternative nobody bought or cooked with is inert, not a warning.
    const membersUncounted = members.filter((m) => !m.counted && !m.inert).map((m) => m.productName);
    const membersOpeningStockBlocked = members
      .filter((m) => ctx.openingBlockedKeys.has(m.key))
      .map((m) => m.productName);
    const hasUnmeasurablePurchases = members.some((m) => unmeasurableKeys.has(m.key));
    const neededUnitMismatch = members.some((m) => needMismatchKeys.has(m.key));
    const countUnitMismatch = members.some((m) => countMismatchKeys.has(m.key));
    // A demand the unit guard dropped is not an absent recipe — `neededUnitMismatch` is
    // what says so, and it already forces the row below 'high' on its own.
    const noRecipeCoverage = !members.some((m) => m.allocatedNeededQty > 0) && !neededUnitMismatch;
    const anyUnpriced = contributing.some((m) => m.pricePerStdUnit === null);

    let confidence: VarianceConfidence;
    if (
      anyUnpriced ||
      noRecipeCoverage ||
      // A member whose demand could not be converted is missing from the pool's usage
      // entirely, so its consumption reads as leakage on this line.
      members.some((m) => needDroppedKeys.has(m.key)) ||
      // …and a member whose own counts cannot be subtracted takes the whole line out of
      // quantity space, leaving only a € reconciliation to judge it by.
      anyMemberQtyUnreadable ||
      membersUncounted.length > 0 ||
      membersExcluded.length > 0
    ) {
      confidence = 'low';
    } else {
      // Never 'high': a pooled row's needs are an allocation, not an observation.
      confidence = 'medium';
    }

    out.push({
      key: `${VARIANCE_POOL_KEY_PREFIX}${poolId}`,
      productName: anchor.productName,
      dishes: Array.from(new Set(members.flatMap((m) => Array.from(ctx.needed.get(m.key)?.dishes ?? [])))).sort(),
      stdUnit: sharedUnit ?? '',
      countUnit: countedRows.every((r) => r.countUnit === countedRows[0].countUnit) ? countedRows[0].countUnit : '',
      ...poolBasis,
      startQty,
      purchasedQty,
      neededQty,
      expectedQty,
      endQty,
      varianceQty,
      pricePerStdUnit: comparable ? contributing.find((m) => m.pricePerStdUnit !== null)?.pricePerStdUnit ?? null : null,
      startValue,
      purchasedValue,
      neededValue,
      expectedValue,
      countedValue,
      varianceValue,
      variancePct:
        expectedValue && expectedValue !== 0 && varianceValue !== null ? varianceValue / expectedValue : null,
      sectionChanges,
      attributedSectionKey,
      attributedSectionName,
      moved,
      mixedSections,
      hasUnmeasurablePurchases,
      noRecipeCoverage,
      ...(neededUnitMismatch ? { neededUnitMismatch: true } : {}),
      ...(countUnitMismatch ? { countUnitMismatch: true } : {}),
      ...(anyMemberQtyUnreadable ? { countQtyUnreadable: true } : {}),
      alsoInGroups: Array.from(new Set(countedRows.flatMap((r) => r.alsoInGroups))).sort(),
      negativeExpected: expectedQty !== null ? expectedQty < 0 : (expectedValue ?? 0) < 0,
      inventoryCategoryName: countedRows.find((r) => r.inventoryCategoryName !== null)?.inventoryCategoryName ?? null,
      confidence,
      status: classifyVarianceStatus(confidence, varianceValue, expectedValue),
      pool: {
        id: poolId,
        signature: memberKeys.join('|'),
        memberCount: memberKeys.length,
        membersUncounted,
        membersExcluded,
        membersInOtherGroups,
        membersOpeningStockBlocked,
        stdUnit: sharedUnit,
        quantitiesComparable: comparable,
      },
      members,
    });
  }

  return out;
}

// --- main entry point -----------------------------------------------------

export function computeInventoryVariance(inputs: VarianceReportInputs): VarianceReportData {
  const {
    countsHistory,
    orders,
    revenue,
    costPriceList,
    subrecipesById,
    defaultWastePercentage,
    currentRestaurantId,
    selection,
    liveCategoryByKey,
    canonicalCountUnits,
  } = inputs;

  const { groupKey, endSessionKey, baselineSessionKey } = selection;
  if (!groupKey || !endSessionKey || !baselineSessionKey || endSessionKey === baselineSessionKey) return EMPTY;

  const sessions = groupCountsIntoSessions(countsHistory);
  const groupSessions = sessions.filter((s) => groupKeyOf(s) === groupKey);
  const endSession = groupSessions.find((s) => s.key === endSessionKey);
  const baselineSession = groupSessions.find((s) => s.key === baselineSessionKey);
  if (!endSession || !baselineSession) return EMPTY;

  const startAt = baselineSession.completedAt.getTime();
  const endAt = endSession.completedAt.getTime();
  const scopeLabel = endSession.groupName ?? baselineSession.groupName ?? '';

  const baseAgg = aggregateSession(baselineSession);
  const endAgg = aggregateSession(endSession);

  // Explode every section before checking product-level opening stock. Moving stock
  // between sections (including newly added sections) does not affect eligibility.
  const explosionCtx: SubrecipeExplosionContext = { subrecipesById, defaultWastePercentage, currentRestaurantId };
  const createLeafAgg = (identity: { name: string; stdUnit: string }): ProductAgg => ({
    totalQty: 0,
    bySection: new Map(),
    // Rule 6 — the prep's own frozen value already stands for this stock, so a leaf holds
    // no € of its own in any section either.
    valueBySection: new Map(),
    name: identity.name,
    stdUnit: identity.stdUnit,
    countUnit: '',
    // A leaf was never counted, so no frozen item can state a pack size for it. Its stock
    // is already in the recipe row's std unit, which is the only space it has.
    stdPerCountUnit: null,
    unitMismatch: false,
    qtyUnreadable: false,
    // No count froze a price for this leaf; the row's price chain falls back to the
    // latest ordered price. `value: 0` keeps rule 6 — the prep's own frozen value
    // already stands for this stock in the session total.
    price: null,
    value: 0,
    inventoryCategoryName: null,
  });
  applySubrecipeExplosion(baseAgg.perProduct, explosionCtx, createLeafAgg);
  applySubrecipeExplosion(endAgg.perProduct, explosionCtx, createLeafAgg);

  // --- purchases in [startAt, endAt], matched on inventory key ---
  const purchases = collectWindowPurchases(orders, startAt, endAt);

  // --- needed-from-menu over the window (R2 needed-index, re-keyed to inventory keys) ---
  const windowRevenue = revenue.filter((r) => {
    const t = new Date(r.date).getTime();
    return !Number.isNaN(t) && t >= startAt && t <= endAt;
  });
  // Interchangeable-pool supply for ingredient alternatives (no-op without any
  // alternatives — the engine short-circuits singleton pools to today's exact math).
  // UNLIKE the utilization report, supply here is window purchases PLUS the opening
  // stock still in hand: a counted product with shelf stock and no purchases would
  // otherwise carry allocation weight 0 and push its entire demand onto a sibling —
  // phantom leakage on one row, phantom over-count on the other, ~net zero headline.
  const openingBlockedKeys = new Set<string>();
  const supply = new Map(Array.from(purchases.entries()).map(([k, v]) => [k, v.baseQty]));
  Array.from(baseAgg.perProduct.entries()).forEach(([key, base]) => {
    // Prep keys are gone by now (the explosion removed them); the guard must not depend
    // on that, and neither key space can ever match an order line.
    if (key.startsWith('custom:') || key.startsWith('subrecipe:') || base.totalQty <= 0) return;
    // A session whose own sections could not be added up has no opening quantity to lend:
    // `totalQty` there covers only the items that fitted one space.
    if (base.qtyUnreadable) {
      openingBlockedKeys.add(key);
      return;
    }
    // An exploded leaf carries the std unit harvested from the recipe row it came from,
    // so this guard rejects a unit mismatch instead of mis-scaling the supply.
    if (openingStockFitsSupply(base.stdUnit, purchases.get(key))) {
      supply.set(key, (supply.get(key) ?? 0) + base.totalQty);
    } else {
      openingBlockedKeys.add(key);
    }
  });
  // The collected needs give the pooled view its membership (`collectPools`) — the
  // allocation itself is unchanged by asking for them.
  const collectedNeeds: AllocationNeed[] = [];
  const sharedNeeded = buildNeededIndex(costPriceList, windowRevenue, subrecipesById, defaultWastePercentage, currentRestaurantId, inventoryKey, supply, collectedNeeds);
  const neededByKey = new Map<string, { qty: number; dishes: Set<string> }>();
  Array.from(sharedNeeded.entries()).forEach(([key, info]) => {
    neededByKey.set(key, { qty: info.base, dishes: new Set(info.dishes) });
  });
  // What the RECIPES know about each product — its name and supplier for pool members
  // nothing counted, and the unit space (plus pack size) every needed quantity above is
  // denominated in, which the row loop must convert into the counts' space.
  const needInfoByKey = collectNeedProductInfo(costPriceList, subrecipesById, inventoryKey);

  // --- cross-group presence: BASE keys held in ANY other group's latest session ---
  const latestByGroup = new Map<string, SessionGroup>();
  sessions.forEach((s) => {
    const k = groupKeyOf(s);
    if (!latestByGroup.has(k)) latestByGroup.set(k, s);
  });
  const alsoInGroupsByKey = new Map<string, Set<string>>();
  Array.from(latestByGroup.entries()).forEach(([gk, s]) => {
    if (gk === groupKey) return;
    const gName = s.groupName ?? gk;
    s.counts.forEach((c) => c.items.forEach((item) => {
      const key = baseProductKey(item.productKey);
      let set = alsoInGroupsByKey.get(key);
      if (!set) { set = new Set(); alsoInGroupsByKey.set(key, set); }
      set.add(gName);
    }));
  });

  // Latest-price fallback walks the full order history newest-first.
  const sortedAllOrders = [...orders].sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());

  // --- product universe = baseline ∪ end counts ONLY ---
  // A purchase-only key gets no PRODUCT row: never-counted stock is not tracked
  // inventory, so its consumption is not leakage. The pooled pass below is the one
  // exception — it pulls in an uncounted pool member's purchases, because within an
  // interchangeable pool that supply demonstrably reached the kitchen (flagged low
  // confidence; see the module doc).
  const allKeys = new Set<string>([
    ...Array.from(baseAgg.perProduct.keys()),
    ...Array.from(endAgg.perProduct.keys()),
  ]);

  const rows: VarianceRow[] = [];
  const customItems: ConsumptionCustomRow[] = [];
  const excluded: VarianceExcludedProduct[] = [];
  const excludedKeys = new Set<string>();
  const unmeasurableNames = new Set<string>();
  // The starred unit per BASE product key for THIS group; absent unless the page asked for
  // the count-unit view (the rows then fall back to their own frozen space).
  const canonicalUnits = canonicalCountUnits?.get(groupKey);

  Array.from(allKeys).forEach((key) => {
    const base = baseAgg.perProduct.get(key);
    const end = endAgg.perProduct.get(key);
    const buy = purchases.get(key);

    // An opening count establishes tracked stock; absence at closing means zero.
    const touchedSections = new Set<string>([
      ...(base ? Array.from(base.bySection.keys()) : []),
      ...(end ? Array.from(end.bySection.keys()) : []),
    ]);
    if (!base) {
      const secNames = Array.from(touchedSections).map(
        (sk) => baseAgg.sectionInfo.get(sk)?.name ?? endAgg.sectionInfo.get(sk)?.name ?? sk
      );
      excluded.push({
        key,
        name: end?.name || buy?.name || key,
        sections: Array.from(new Set(secNames)).sort(),
        startValue: 0,
        endValue: end?.value ?? 0,
      });
      excludedKeys.add(key);
      return;
    }

    // The baseline and the end count are two INDEPENDENT freezes of this product, each in
    // whatever unit space its assignment held AT THAT MOMENT — nothing rewrites a frozen
    // count when the assignment later changes. Re-express the baseline in the END count's
    // space (the space this row reports, and the space its €/std-unit is frozen per), or
    // refuse to subtract the two at all. See `countSpaceBridge`.
    const alignment = alignCountSpaces(
      base ? { totalQty: base.totalQty, space: spaceOf(base) } : undefined,
      end ? { space: spaceOf(end) } : undefined
    );
    // A session that could not even add its own sections up is unreadable on its own.
    const countQtyUnreadable = !!base?.qtyUnreadable || !!end?.qtyUnreadable || alignment.factor === null;
    const countUnitMismatch = !!base?.unitMismatch || !!end?.unitMismatch || alignment.mismatch;
    const startFactor = alignment.factor ?? 1;

    const startQty = (base?.totalQty ?? 0) * startFactor;
    const endQty = end?.totalQty ?? 0;
    const stdUnit = end?.stdUnit || base?.stdUnit || '';
    const countUnit = end?.countUnit || base?.countUnit || '';
    const name = end?.name || base?.name || buy?.name || '';
    const displayBasis = resolveDisplayBasis(canonicalUnits?.get(key), stdUnit, frozenBasisOf(end, base));

    // Custom items never match orders → count deltas only, out of headline leakage.
    if (key.startsWith('custom:')) {
      const price = end?.price ?? base?.price ?? null;
      const deltaQty = endQty - startQty;
      customItems.push({
        key,
        productName: name,
        stdUnit,
        // Two counts in unbridgeable spaces have no delta between them — null, not 0.
        startQty: countQtyUnreadable ? null : startQty,
        endQty: countQtyUnreadable ? null : endQty,
        deltaQty: countQtyUnreadable ? null : deltaQty,
        deltaEur: price !== null && !countQtyUnreadable ? deltaQty * price : null,
        ...(countQtyUnreadable ? { countQtyUnreadable: true } : {}),
        // Optional on the SHARED row type: only leakage resolves a basis, so the R7
        // Consumption report's output stays exactly as it was.
        ...(displayBasis.displayCountUnit !== null
          ? {
              displayCountUnit: displayBasis.displayCountUnit,
              displayStdPerCountUnit: displayBasis.displayStdPerCountUnit as number,
            }
          : {}),
      });
      return;
    }

    // Purchases must be read in the unit space THIS count was frozen in — a count frozen
    // as bottles cannot be reconciled against an order line measured in millilitres.
    const { qty: purchasedQty, unitMismatch } = purchasedQtyForCount(buy, stdUnit);
    const need = neededByKey.get(key);
    // The recipe quantities are a SECOND snapshot of the same supplier line and drift the
    // same way — guard them in the same space before they touch expected stock.
    const {
      qty: neededQty,
      unitMismatch: neededUnitMismatch,
      factor: neededUnitFactor,
    } = neededQtyForCount(need?.qty ?? 0, needInfoByKey.get(key), stdUnit);
    const expectedQty = startQty + purchasedQty - neededQty;
    const varianceQty = endQty - expectedQty;

    // Price chain: end frozen → baseline frozen → latest order fallback.
    const price = end?.price ?? base?.price ?? latestPriceForKey(key, sortedAllOrders);
    const val = (q: number): number | null => (price !== null ? q * price : null);

    // --- section layer (facts) ---
    const sectionKeys = new Set<string>([
      ...(base ? Array.from(base.bySection.keys()) : []),
      ...(end ? Array.from(end.bySection.keys()) : []),
    ]);
    const sectionChanges: VarianceSectionChange[] = Array.from(sectionKeys).map((sk) => {
      const info = baseAgg.sectionInfo.get(sk) ?? endAgg.sectionInfo.get(sk);
      const identity = { sectionKey: sk, sectionName: info?.name ?? sk, deleted: info?.deleted ?? false };
      if (countQtyUnreadable) {
        // There is no quantity space to subtract in, so the section layer switches to the
        // frozen € each count actually recorded — declared through `endValue`, exactly as
        // a mixed-unit pooled row declares it. The quantities stay at placeholder zeroes
        // that `hasComparableQty` keeps off every screen.
        const sStartValue = base?.valueBySection.get(sk) ?? 0;
        const sEndValue = end?.valueBySection.get(sk) ?? 0;
        return { ...identity, startQty: 0, endQty: 0, deltaQty: 0, deltaValue: sEndValue - sStartValue, endValue: sEndValue };
      }
      const sStart = (base?.bySection.get(sk) ?? 0) * startFactor;
      const sEnd = end?.bySection.get(sk) ?? 0;
      const deltaQty = sEnd - sStart;
      return {
        ...identity,
        startQty: sStart,
        endQty: sEnd,
        deltaQty,
        deltaValue: price !== null ? deltaQty * price : null,
      };
    });
    sectionChanges.sort((a, b) => a.sectionName.localeCompare(b.sectionName));

    // Movement and attribution are read in € on an unreadable row — the one space it has.
    const deltaOf = (c: VarianceSectionChange): number => (countQtyUnreadable ? c.deltaValue ?? 0 : c.deltaQty);
    // moved: in ≥2 sections and offsetting deltas.
    const netDelta = sectionChanges.reduce((s, c) => s + deltaOf(c), 0);
    const absDelta = sectionChanges.reduce((s, c) => s + Math.abs(deltaOf(c)), 0);
    const moved = sectionChanges.length >= 2 && absDelta > Math.abs(netDelta) + EPS;

    // Attribution rules 1/2/3.
    let attributedSectionKey: string | null = null;
    let mixedSections = false;
    if (sectionChanges.length === 1) {
      attributedSectionKey = sectionChanges[0].sectionKey; // rule 1
    } else if (sectionChanges.length >= 2) {
      const changed = sectionChanges.filter((c) => Math.abs(deltaOf(c)) > EPS);
      if (changed.length === 1) {
        attributedSectionKey = changed[0].sectionKey; // rule 2
      } else {
        mixedSections = true; // rule 3 (0 or ≥2 changed sections → not attributable)
      }
    }
    const attributedSectionName = attributedSectionKey
      ? sectionChanges.find((c) => c.sectionKey === attributedSectionKey)?.sectionName ?? null
      : null;

    // --- flags / confidence / status ---
    // A unit mismatch is the same user-facing story as an unmeasurable line — purchases
    // could not be counted for this product — so it rides the existing flag/confidence path.
    const hasUnmeasurablePurchases = !!buy?.hadUnmeasurable || unitMismatch;
    if (hasUnmeasurablePurchases) unmeasurableNames.add(name);
    // A usage the unit guard had to drop is NOT "this product is in no recipe" — the
    // recipes are there, they just cannot be read in this count's space, and the row says
    // that through `neededUnitMismatch` instead.
    const noRecipeCoverage = neededQty === 0 && !neededUnitMismatch;
    const alsoInGroups = Array.from(alsoInGroupsByKey.get(key) ?? []).sort();
    // Only when EVERY session that holds this key holds it as exploded prep stock: a
    // product counted loose in either count has a real shelf and reconciles normally.
    const anyExploded = !!base?.onlyFromSubrecipe || !!end?.onlyFromSubrecipe;
    const countedLoose = (!!base && !base.onlyFromSubrecipe) || (!!end && !end.onlyFromSubrecipe);
    const onlyFromSubrecipe = anyExploded && !countedLoose;

    // A dropped usage leaves expected = start + purchases, so ALL consumption reads as
    // leakage — as untrustworthy as having no recipe at all. A CONVERTED one is exact but
    // sits on a stale snapshot, so it lands where the other "read it in another space"
    // caveats live.
    const neededUnreadable = !!neededUnitMismatch && neededQty === 0;
    // Two counts that cannot be subtracted leave nothing to check the € reconciliation
    // against, so the row is as untrustworthy as an unpriced one. A CONVERTED pair is
    // exact but sits on a stale snapshot, and lands with the other rebasing caveats.
    let confidence: VarianceConfidence;
    if (price === null || noRecipeCoverage || onlyFromSubrecipe || neededUnreadable || countQtyUnreadable)
      confidence = 'low';
    else if (hasUnmeasurablePurchases || neededUnitMismatch || countUnitMismatch || alsoInGroups.length > 0)
      confidence = 'medium';
    else confidence = 'high';

    // € reconciles even when the quantities cannot: a frozen value is a fact OF its own
    // count, whatever unit that count was taken in. So an unreadable row still carries
    // real money on every line — the mixed-unit pool convention, on a product row.
    const frozenStartValue = base?.value ?? 0;
    const frozenCountedValue = end?.value ?? 0;
    const startValue = countQtyUnreadable ? frozenStartValue : val(startQty);
    const countedValue = countQtyUnreadable ? frozenCountedValue : val(endQty);
    const purchasedValue = val(purchasedQty);
    const neededValue = val(neededQty);
    const expectedValue = countQtyUnreadable
      ? purchasedValue === null || neededValue === null
        ? null
        : frozenStartValue + purchasedValue - neededValue
      : val(expectedQty);
    const varianceValue = countQtyUnreadable
      ? expectedValue === null
        ? null
        : frozenCountedValue - expectedValue
      : val(varianceQty);
    const negativeExpected = countQtyUnreadable ? (expectedValue ?? 0) < 0 : expectedQty < 0;
    const status = classifyVarianceStatus(confidence, varianceValue, expectedValue);

    rows.push({
      key,
      productName: name,
      dishes: need ? Array.from(need.dishes).sort() : [],
      stdUnit,
      countUnit,
      ...displayBasis,
      // Null, never 0: "no quantity exists in any single space" and "the shelf is empty"
      // are opposite statements, and a 0 here reads as the second one.
      startQty: countQtyUnreadable ? null : startQty,
      purchasedQty: countQtyUnreadable ? null : purchasedQty,
      neededQty: countQtyUnreadable ? null : neededQty,
      expectedQty: countQtyUnreadable ? null : expectedQty,
      endQty: countQtyUnreadable ? null : endQty,
      varianceQty: countQtyUnreadable ? null : varianceQty,
      pricePerStdUnit: price,
      startValue,
      purchasedValue,
      neededValue,
      expectedValue,
      countedValue,
      varianceValue,
      variancePct: expectedValue && expectedValue !== 0 && varianceValue !== null ? varianceValue / expectedValue : null,
      sectionChanges,
      attributedSectionKey,
      attributedSectionName,
      moved,
      mixedSections,
      hasUnmeasurablePurchases,
      noRecipeCoverage,
      ...(neededUnitMismatch ? { neededUnitMismatch: true } : {}),
      ...(neededUnitFactor ? { neededUnitFactor } : {}),
      ...(countUnitMismatch ? { countUnitMismatch: true } : {}),
      ...(countQtyUnreadable ? { countQtyUnreadable: true } : {}),
      alsoInGroups,
      negativeExpected,
      ...(onlyFromSubrecipe ? { onlyFromSubrecipe: true } : {}),
      inventoryCategoryName: end?.inventoryCategoryName ?? base?.inventoryCategoryName ?? null,
      confidence,
      status,
    });
  });

  // --- alternative pools: one reconciled row replaces its counted members' rows ---
  // Without any alternatives `collectPools` yields only singleton pools, nothing is
  // built and `rows` is untouched — product rows stay bit-identical.
  const primaryKeys = new Set<string>();
  for (const n of collectedNeeds) if (n.options.length > 0) primaryKeys.add(n.options[0].key);
  const pooledRows = buildPooledVarianceRows({
    pools: collectPools(collectedNeeds),
    rowsByKey: new Map(rows.map((r) => [r.key, r])),
    excludedKeys,
    purchases,
    needed: sharedNeeded,
    primaryKeys,
    openingBlockedKeys,
    needInfoByKey,
    latestPrice: (key: string) => latestPriceForKey(key, sortedAllOrders),
    alsoInGroupsByKey,
    canonicalUnits,
  });
  if (pooledRows.length > 0) {
    const pooledMemberKeys = new Set(
      pooledRows.flatMap((r) => (r.members ?? []).filter((m) => m.counted).map((m) => m.key))
    );
    const solo = rows.filter((r) => !pooledMemberKeys.has(r.key));
    rows.length = 0;
    for (const r of solo) rows.push(r);
    for (const r of pooledRows) rows.push(r);
  }

  // --- headline totals ---
  // start/counted = the two counts' frozen totals (exactly what the Count Review
  // page shows). Leakage = Σ row variance (null-price rows add 0 — assumed
  // unchanged). expected = counted − leakage, and `adjustmentValue` closes the
  // gap between (start + purchases − usage) and expected: the count change of
  // assumed-unchanged products plus price revaluation between the two counts.
  const startTotalValue = baselineSession.totalValue;
  const countedTotalValue = endSession.totalValue;
  let varianceTotalValue = 0;
  let purchasedTotalValue = 0;
  let neededTotalValue = 0;
  let highRiskCount = 0;
  let absVarianceTotal = 0;
  for (const r of rows) {
    varianceTotalValue += r.varianceValue ?? 0;
    purchasedTotalValue += r.purchasedValue ?? 0;
    neededTotalValue += r.neededValue ?? 0;
    if (r.status === 'highRisk') highRiskCount += 1;
    if (r.varianceValue !== null) absVarianceTotal += Math.abs(r.varianceValue);
  }
  const expectedTotalValue = countedTotalValue - varianceTotalValue;
  const adjustmentValue = expectedTotalValue - (startTotalValue + purchasedTotalValue - neededTotalValue);

  // Linked share = end-count products with a usable price (the same products a
  // real variance can be computed for), over all end-count products. A leaf that only
  // exists because a prep was exploded into it is not counted stock and belongs in
  // neither figure — nobody can "link" a product the count never listed.
  let totalProductCount = 0;
  let linkedProductCount = 0;
  Array.from(endAgg.perProduct.entries()).forEach(([key, p]) => {
    if (p.onlyFromSubrecipe) return;
    totalProductCount += 1;
    if (key.startsWith('custom:')) return;
    const price = p.price ?? baseAgg.perProduct.get(key)?.price ?? latestPriceForKey(key, sortedAllOrders);
    if (price !== null) linkedProductCount += 1;
  });

  // --- section aggregation (reading A: whole-product variance → its section) ---
  const sectionAgg = new Map<string, { name: string; deleted: boolean; expected: number; counted: number; variance: number; change: number }>();
  const ensureSection = (sk: string, nm: string, del: boolean) => {
    let s = sectionAgg.get(sk);
    if (!s) { s = { name: nm, deleted: del, expected: 0, counted: 0, variance: 0, change: 0 }; sectionAgg.set(sk, s); }
    return s;
  };
  let unattrExpected = 0;
  let unattrCounted = 0;
  let unattrVariance = 0;

  for (const r of rows) {
    const price = r.pricePerStdUnit;
    for (const sc of r.sectionChanges) {
      const s = ensureSection(sc.sectionKey, sc.sectionName, sc.deleted);
      s.change += sc.deltaValue ?? 0;
      if (sc.sectionKey !== r.attributedSectionKey) {
        // Frozen / non-attributed section: counted = expected = end stock (variance 0).
        // A row that carries `endValue` states that € directly, because `endQty × price`
        // would be wrong for it — a pool prices per member, and an unreadable row has no
        // quantity to multiply at all.
        const frozenValue = sc.endValue !== undefined ? sc.endValue ?? 0 : price !== null ? sc.endQty * price : null;
        if (frozenValue !== null) {
          s.expected += frozenValue;
          s.counted += frozenValue;
        }
      }
    }
    if (r.attributedSectionKey) {
      const s = sectionAgg.get(r.attributedSectionKey)!;
      s.expected += r.expectedValue ?? 0;
      s.counted += r.countedValue ?? 0;
      s.variance += r.varianceValue ?? 0;
    } else {
      unattrExpected += r.expectedValue ?? 0;
      unattrCounted += r.countedValue ?? 0;
      unattrVariance += r.varianceValue ?? 0;
    }
  }

  const sections: VarianceSectionRow[] = Array.from(sectionAgg.entries())
    .map(([sk, s]) => ({
      sectionKey: sk,
      sectionName: s.name,
      deleted: s.deleted,
      expectedValue: s.expected,
      countedValue: s.counted,
      varianceValue: s.variance,
      variancePct: s.expected !== 0 ? s.variance / s.expected : null,
      changeValue: s.change,
    }))
    .sort((a, b) => a.varianceValue - b.varianceValue);
  sections.push({
    sectionKey: UNATTRIBUTED_KEY,
    sectionName: '',
    deleted: false,
    expectedValue: unattrExpected,
    countedValue: unattrCounted,
    varianceValue: unattrVariance,
    variancePct: unattrExpected !== 0 ? unattrVariance / unattrExpected : null,
    changeValue: 0,
  });

  // --- category aggregation (both modes) ---
  const buildCategories = (mode: 'inventory' | 'product', nameOf: (r: VarianceRow) => string): VarianceCategoryRow[] => {
    const map = new Map<string, { expected: number; counted: number; variance: number }>();
    for (const r of rows) {
      const nm = nameOf(r);
      let c = map.get(nm);
      if (!c) { c = { expected: 0, counted: 0, variance: 0 }; map.set(nm, c); }
      c.expected += r.expectedValue ?? 0;
      c.counted += r.countedValue ?? 0;
      c.variance += r.varianceValue ?? 0;
    }
    return Array.from(map.entries())
      .map(([name, c]) => ({ name, mode, expectedValue: c.expected, countedValue: c.counted, varianceValue: c.variance }))
      .sort((a, b) => a.varianceValue - b.varianceValue);
  };
  // A pooled row's key is a pool id, not a product key — read the live category off its
  // members instead, so pools don't all pile into "unknown".
  const liveCategoryOf = (r: VarianceRow): string => {
    const direct = liveCategoryByKey?.get(r.key);
    if (direct) return direct;
    for (const m of r.members ?? []) {
      const memberCategory = liveCategoryByKey?.get(m.key);
      if (memberCategory) return memberCategory;
    }
    return 'unknown';
  };
  const categories: VarianceCategoryRow[] = [
    ...buildCategories('inventory', (r) => r.inventoryCategoryName ?? 'Uncategorized'),
    ...buildCategories('product', liveCategoryOf),
  ];

  rows.sort((a, b) => (a.varianceValue ?? Infinity) - (b.varianceValue ?? Infinity));
  customItems.sort((a, b) => Math.abs(b.deltaEur ?? 0) - Math.abs(a.deltaEur ?? 0));
  excluded.sort((a, b) => b.endValue - a.endValue);

  return {
    hasData: true,
    window: {
      scope: 'group',
      label: scopeLabel,
      startLabel: getTableFormattedDate(new Date(startAt)),
      endLabel: getTableFormattedDate(new Date(endAt)),
      startAt,
      endAt,
    },
    rows,
    customItems,
    sections,
    categories,
    excluded,
    expectedTotalValue,
    countedTotalValue,
    varianceTotalValue,
    purchasedTotalValue,
    neededTotalValue,
    startTotalValue,
    adjustmentValue,
    highRiskCount,
    confidenceScore: totalProductCount > 0 ? linkedProductCount / totalProductCount : 0,
    linkedProductCount,
    totalProductCount,
    unattributedVarianceValue: unattrVariance,
    unattributedShare: absVarianceTotal > 0 ? Math.abs(unattrVariance) / absVarianceTotal : 0,
    unmeasurableProductNames: Array.from(unmeasurableNames).sort(),
  };
}

// --- all-groups aggregation -----------------------------------------------

export interface VarianceGroupResult {
  groupKey: string;
  groupName: string | null;
  data: VarianceReportData;
}

export interface VarianceCombined {
  rows: (VarianceRow & { groupName: string | null })[];
  sections: VarianceSectionRow[];
  expectedTotalValue: number;
  countedTotalValue: number;
  varianceTotalValue: number;
  purchasedTotalValue: number;
  neededTotalValue: number;
  startTotalValue: number;
  adjustmentValue: number;
  highRiskCount: number;
  unattributedVarianceValue: number;
  confidenceScore: number;
  linkedProductCount: number;
  totalProductCount: number;
  unattributedShare: number;
}

export interface VarianceAllData {
  perGroup: VarianceGroupResult[];
  combined: VarianceCombined;
  skippedGroups: { groupKey: string; groupName: string | null }[];
}

export type VarianceAllInputs = Omit<VarianceReportInputs, 'selection'>;

/** Run the per-group computation for every group with ≥2 sessions (baseline = previous session). */
export function computeInventoryVarianceAll(inputs: VarianceAllInputs): VarianceAllData {
  const { countsHistory } = inputs;
  const sessions = groupCountsIntoSessions(countsHistory); // newest-first

  // Bucket sessions by group, newest-first preserved.
  const byGroup = new Map<string, SessionGroup[]>();
  const nameByGroup = new Map<string, string | null>();
  sessions.forEach((s) => {
    const k = groupKeyOf(s);
    const list = byGroup.get(k) ?? [];
    list.push(s);
    byGroup.set(k, list);
    if (!nameByGroup.has(k)) nameByGroup.set(k, s.groupName ?? null);
  });

  const perGroup: VarianceGroupResult[] = [];
  const skippedGroups: { groupKey: string; groupName: string | null }[] = [];

  Array.from(byGroup.entries()).forEach(([groupKey, list]) => {
    const groupName = nameByGroup.get(groupKey) ?? null;
    if (list.length < 2) {
      skippedGroups.push({ groupKey, groupName });
      return;
    }
    const [end, baseline] = list; // newest, previous
    const data = computeInventoryVariance({
      ...inputs,
      selection: { groupKey, endSessionKey: end.key, baselineSessionKey: baseline.key },
    });
    if (data.hasData) perGroup.push({ groupKey, groupName, data });
  });

  const combined: VarianceCombined = {
    rows: [],
    sections: [],
    expectedTotalValue: 0,
    countedTotalValue: 0,
    varianceTotalValue: 0,
    purchasedTotalValue: 0,
    neededTotalValue: 0,
    startTotalValue: 0,
    adjustmentValue: 0,
    highRiskCount: 0,
    unattributedVarianceValue: 0,
    confidenceScore: 0,
    linkedProductCount: 0,
    totalProductCount: 0,
    unattributedShare: 0,
  };
  let absVarianceTotal = 0;
  for (const g of perGroup) {
    const d = g.data;
    combined.rows.push(...d.rows.map((r) => ({ ...r, groupName: g.groupName })));
    combined.sections.push(...d.sections);
    combined.expectedTotalValue += d.expectedTotalValue;
    combined.countedTotalValue += d.countedTotalValue;
    combined.varianceTotalValue += d.varianceTotalValue;
    combined.purchasedTotalValue += d.purchasedTotalValue;
    combined.neededTotalValue += d.neededTotalValue;
    combined.startTotalValue += d.startTotalValue;
    combined.adjustmentValue += d.adjustmentValue;
    combined.highRiskCount += d.highRiskCount;
    combined.unattributedVarianceValue += d.unattributedVarianceValue;
    combined.linkedProductCount += d.linkedProductCount;
    combined.totalProductCount += d.totalProductCount;
    for (const r of d.rows) {
      if (r.varianceValue !== null) absVarianceTotal += Math.abs(r.varianceValue);
    }
  }
  combined.rows.sort((a, b) => (a.varianceValue ?? Infinity) - (b.varianceValue ?? Infinity));
  combined.confidenceScore = combined.totalProductCount > 0 ? combined.linkedProductCount / combined.totalProductCount : 0;
  combined.unattributedShare = absVarianceTotal > 0 ? Math.abs(combined.unattributedVarianceValue) / absVarianceTotal : 0;

  return { perGroup, combined, skippedGroups };
}

// --- per-dish usage (drawer data) -----------------------------------------

export interface PerDishUsage {
  label: string;
  unitsSold: number;
  basePerUnit: number;
  totalBase: number;
  /** Routes this dish reaches the product by, so the drawer can explain `basePerUnit`
   *  (Σ of their `perPortionBase` is it). Empty when every route is direct — and also
   *  when the row's lines had to be rescaled (pool estimate / count-unit conversion),
   *  where exact prep arithmetic would contradict the approximated figure it explains.
   *  Optional, and absent means the same as empty: synthesised rows (zero-sold setup
   *  cards) carry no routes, and a missing breakdown degrades to the plain card. */
  prepPaths?: NeedPath[];
}

/**
 * Per inventory key → the dishes/choices that consumed it in the window, with the
 * base units each contributed. Reuses the exact sales extraction + override-first
 * precedence of `weightNeedsBySales`, so Σ totalBase per key equals `buildNeededIndex`'s
 * base for that key over the same revenue.
 *
 * `perUnit` MUST have been built with the `inventoryKey` keyer (see LeakageTab), so its
 * `need.key`s are already raw inventory keys — the same space as the variance rows.
 *
 * Pass the report's `rows` to make the map POOL-AWARE: every pooled member's demand is
 * reported once under the pool row's key (`pool:<id>`) instead of per member, and the
 * lines are scaled to the row's allocated `neededQty` so the drawer's breakdown sums to
 * the row total. Mixed-unit pools get no lines at all — their quantities do not add up
 * in any single unit, so a per-dish base figure would be meaningless.
 *
 * In the all-groups view pass the rows of the SAME group as `windowRevenue`: a pool key
 * repeats across groups, and a key is scaled once, off the first row carrying it.
 */
export function buildPerDishUsage(
  perUnit: PerUnitNeedsIndex,
  windowRevenue: RevenueHistory[],
  rows?: VarianceRow[]
): Map<string, PerDishUsage[]> {
  const { salesByMenuItem, choiceSalesByMenuItem } = extractSalesFromRevenue(windowRevenue);
  const out = new Map<string, PerDishUsage[]>();
  const push = (invKey: string, usage: PerDishUsage) => {
    const list = out.get(invKey) ?? [];
    list.push(usage);
    out.set(invKey, list);
  };

  // Pooled row per key, first wins: the all-groups view repeats a pool key across
  // groups, and scaling the same lines once per group would multiply them twice over.
  const poolRowByKey = new Map<string, VarianceRow>();
  for (const r of rows ?? []) {
    if (r.pool && !poolRowByKey.has(r.key)) poolRowByKey.set(r.key, r);
  }
  // Member key → the pooled row that owns it (empty without a pool-aware caller).
  const poolKeyByMember = new Map<string, string>();
  for (const r of Array.from(poolRowByKey.values())) {
    for (const m of r.members ?? []) poolKeyByMember.set(m.key, r.key);
  }

  // Leakage has no purchase context → primary-only (options[0]), collapsed per key so
  // each dish contributes one row per product (identical to the pre-alternatives shape).
  // Pooled members collapse further, onto their pool: within a pool the split between
  // products is the allocator's estimate, so only the pool's total is worth showing.
  const collapsePrimary = (needs: PerUnitNeed[]): Map<string, { base: number; paths: Map<string, NeedPath> }> => {
    const perKey = new Map<string, { base: number; paths: Map<string, NeedPath> }>();
    for (const n of needs) {
      const primary = n.options[0];
      if (!primary || !primary.key) continue;
      const target = poolKeyByMember.get(primary.key) ?? primary.key;
      const cur = perKey.get(target) ?? { base: 0, paths: new Map<string, NeedPath>() };
      cur.base += n.portions * primary.base;
      // `portions` here is the prep chain alone (this index is per 1 unit sold), so the
      // contribution is ALREADY per-portion — no divisor to apply, unlike utilization.
      addNeedPath(cur.paths, n.hops, n.portions * primary.base);
      perKey.set(target, cur);
    }
    return perKey;
  };

  for (const d of perUnit.dishes) {
    const unitsSold = salesByMenuItem.get(d.menuItemName) ?? 0;
    if (unitsSold <= 0) continue;
    for (const [key, { base, paths }] of Array.from(collapsePrimary(d.needs).entries())) {
      push(key, {
        label: d.menuItemName,
        unitsSold,
        basePerUnit: base,
        totalBase: unitsSold * base,
        prepPaths: finalizeNeedPaths(paths),
      });
    }
  }

  const overrideByPair = new Map(perUnit.choiceOverrides.map((o) => [`${o.menuItemName} ${o.choiceName}`, o.needs]));
  const defaultByChoice = new Map(perUnit.choiceDefaults.map((c) => [c.choiceName, c.needs]));
  for (const [menuItemName, picks] of Array.from(choiceSalesByMenuItem.entries())) {
    for (const [choiceName, qtyPicked] of Array.from(picks.entries())) {
      if (qtyPicked <= 0) continue;
      const needs = overrideByPair.get(`${menuItemName} ${choiceName}`) ?? defaultByChoice.get(choiceName);
      if (!needs) continue;
      for (const [key, { base, paths }] of Array.from(collapsePrimary(needs).entries())) {
        push(key, {
          label: `${menuItemName} — ${choiceName}`,
          unitsSold: qtyPicked,
          basePerUnit: base,
          totalBase: qtyPicked * base,
          prepPaths: finalizeNeedPaths(paths),
        });
      }
    }
  }

  // A pool's lines are built from each need's PRIMARY per-portion amount, while the row
  // total is what the allocator actually handed the pool (members can differ per
  // portion). Scale the lines onto that total so the breakdown explains the row instead
  // of contradicting it — the same proportional-estimate contract as the row itself.
  //
  // A row whose recipe unit space had to be converted into its count's needs the exact
  // same treatment: its lines are raw recipe quantities (3000 ml) while the row total is
  // in the count's space (1 bottle), and rendering the lines against the ROW's unit is
  // literally how "3000 pcs per dish" reached a drinks drawer.
  const scaledRowByKey = new Map<string, VarianceRow>(poolRowByKey);
  for (const r of rows ?? []) {
    if ((r.neededUnitMismatch || r.countQtyUnreadable) && !scaledRowByKey.has(r.key)) scaledRowByKey.set(r.key, r);
  }
  for (const r of Array.from(scaledRowByKey.values())) {
    const lines = out.get(r.key);
    if (!lines) continue;
    // No usage survived the guard (mixed-unit pool, a conversion that does not exist, or
    // two counts with no common space to report a usage in): there is no honest per-dish
    // number to print, and a zeroed card reads as "this dish uses none of it" — which is
    // the opposite of what happened.
    if ((r.pool && !r.pool.quantitiesComparable) || r.countQtyUnreadable || (r.neededUnitMismatch && !r.neededQty)) {
      out.delete(r.key);
      continue;
    }
    // Whatever happens to the numbers below, the prep lines cannot come along: they state
    // exact recipe arithmetic (`rowBase × hop ratios = per dish`), and once a figure is a
    // pool estimate or a cross-unit conversion that identity no longer holds against what
    // the card shows. Dropped here rather than scaled — a rescaled "250 g of a 500 g batch"
    // would be a fabricated recipe, not an approximation.
    lines.forEach((line) => {
      line.prepPaths = [];
    });
    const total = lines.reduce((s, u) => s + u.totalBase, 0);
    if (total <= 0) continue;
    const factor = (r.neededQty ?? 0) / total;
    for (const line of lines) {
      line.totalBase *= factor;
      line.basePerUnit *= factor;
    }
  }

  Array.from(out.values()).forEach((list) => list.sort((a, b) => b.totalBase - a.totalBase));
  return out;
}
